import { useDownloadFileMutation } from '@/hooks/useDownloadFileMutation';
import { createChatExport, CreateChatExportRequest, getChatExportStatus } from '@/services/api/did-api/didApi';
import JSZip from 'jszip';
import { useMutation } from 'react-query';
import { createCsvBlobUrl, generateCsvFromChats } from '../utils/csvGenerator';

interface ChatMessage {
    role: string;
    content: string;
    created_at: string;
}

interface ChatData {
    chatId: string;
    agentId?: string;
    messages: ChatMessage[];
}

export const useExportChats = () => {
    const downloadMutation = useDownloadFileMutation();

    const exportMutation = useMutation<void, Error, CreateChatExportRequest>(async (payload: CreateChatExportRequest) => {
        const resultUrls = await pollForCompletion(payload);

        if (resultUrls.length === 0) {
            throw new Error('No result URLs received from export');
        }

        const allChatsData = await downloadAndProcessZips(resultUrls);

        if (allChatsData.length === 0) {
            throw new Error('No chat data found in exported files');
        }

        const csvContent = generateCsvFromChats(allChatsData, payload.agent_id);
        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `agent_${payload.agent_id}_chats_${timestamp}`;

        const csvBlobUrl = createCsvBlobUrl(csvContent);

        try {
            await downloadMutation.mutateAsync({
                dataurl: csvBlobUrl,
                filename: filename,
                extension: '.csv',
            });
        } finally {
            URL.revokeObjectURL(csvBlobUrl);
        }
    });

    const pollForCompletion = async (initialPayload: CreateChatExportRequest): Promise<string[]> => {
        const resultUrls = new Set<string>();
        const maxAttempts = 360; // 3 minutes max (360 * 500ms)
        let attempts = 0;

        let { export_id: currentExportId } = await createChatExport(initialPayload);

        while (attempts < maxAttempts) {
            attempts++;

            await new Promise(resolve => setTimeout(resolve, 500));

            const status = await getChatExportStatus(currentExportId);

            if (status.status === 'error') {
                throw new Error(status.error || 'Export failed');
            }

            if (status.result?.result_url) {
                resultUrls.add(status.result.result_url);
            }

            if (status.status === 'done') {
                return Array.from(resultUrls);
            }

            if (status.status === 'partial' && status.result?.token) {
                const newPayload = {
                    ...initialPayload,
                    token: status.result.token,
                };
                const newExport = await createChatExport(newPayload);
                currentExportId = newExport.export_id;
            }
        }

        throw new Error('Export timed out after 3 minutes');
    };

    const downloadAndProcessZips = async (urls: string[]): Promise<ChatData[]> => {
        const allChatsData: ChatData[] = [];

        for (let i = 0; i < urls.length; i++) {
            const url = urls[i];

            try {
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`Failed to download file ${i + 1}`);
                }

                const blob = await response.blob();
                const zip = new JSZip();
                const zipContent = await zip.loadAsync(blob);

                const jsonFiles = Object.keys(zipContent.files).filter(filename => filename.endsWith('.json'));

                for (const filename of jsonFiles) {
                    const file = zipContent.files[filename];
                    const content = await file.async('string');

                    try {
                        const data = JSON.parse(content);
                        const chatId = data.chatId || filename.replace('.json', '');

                        allChatsData.push({
                            chatId,
                            agentId: data.agentId,
                            messages: data.messages || [],
                        });
                    } catch (parseErr) {
                        throw new Error(`Failed to parse ${filename}`);
                    }
                }
            } catch (err: any) {
                throw new Error(err.message || `Failed to process file ${i + 1}`);
            }
        }

        return allChatsData;
    };

    return {
        exportChats: exportMutation.mutateAsync,
        isExporting: exportMutation.isLoading,
        error: exportMutation.error?.message || null,
        clearError: exportMutation.reset,
    };
};
